// Product card swatch window. Hovering (hover devices) previews a variant on the card; clicking or
// tapping selects it. Each swatch button carries the variant's url and image srcset; price and badge
// markup come from <template data-price|data-badge="variantId"> children.
// The track is sized to a whole number of swatches (up to data-max, fewer when the card is narrow), so
// no swatch is ever cut off; --prev/--next commands page by exactly one window and only show on overflow.
class CardSwatches extends HTMLElement {
  #controller = null;
  #resize = null;
  #selected = null;
  #step = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.card = this.closest('[data-product-card]');
    this.track = this.querySelector('[data-swatch-track]');
    if (!this.card || !this.track) return;

    this.#selected = this.querySelector('[data-swatch][aria-pressed="true"]') || this.querySelector('[data-swatch]');
    const canHover = window.matchMedia('(hover: hover)');

    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-swatch]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      this.#selected = button;
      for (const swatch of this.querySelectorAll('[data-swatch]')) {
        swatch.setAttribute('aria-pressed', String(swatch === button));
      }
      this.#apply(button);
    }, { signal });

    this.addEventListener('pointerover', (event) => {
      if (!canHover.matches || event.pointerType === 'touch') return;
      const button = event.target.closest('[data-swatch]');
      if (button && this.contains(button)) this.#apply(button);
    }, { signal });

    this.addEventListener('pointerleave', () => {
      if (this.#selected) this.#apply(this.#selected);
    }, { signal });

    this.addEventListener('command', (event) => {
      if (event.command !== '--next' && event.command !== '--prev') return;
      const last = Math.max(0, Math.ceil(this.querySelectorAll('[data-swatch]').length / this.#step) - 1);
      this.page = Math.min(last, Math.max(0, (this.page || 0) + (event.command === '--next' ? 1 : -1)));
      this.#showPage();
    }, { signal });

    this.#resize = new ResizeObserver(() => this.#layout());
    this.#resize.observe(this);
    if (this.card) this.#resize.observe(this.card);
    requestAnimationFrame(() => this.#layout());
    window.addEventListener('load', () => this.#layout(), { once: true, signal });
    this.#layout();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#resize?.disconnect();
  }

  // Render whole pages rather than a scroll viewport: off-page circles cannot peek through padding,
  // snap offsets, fractional widths, or an in-progress smooth scroll.
  #layout() {
    const swatches = [...this.querySelectorAll('[data-swatch]')];
    if (!swatches.length) return;
    const trackStyle = getComputedStyle(this.track);
    const gap = parseFloat(trackStyle.columnGap) || 0;
    const pad = parseFloat(trackStyle.paddingInlineStart) + parseFloat(trackStyle.paddingInlineEnd);
    const size = parseFloat(getComputedStyle(swatches[0]).width) || 24;
    const unit = size + gap;
    const cap = Math.max(1, Number(this.dataset.max) || swatches.length);
    const width = this.clientWidth;
    const overflow = swatches.length > cap || swatches.length * unit - gap + pad > width;
    const arrow = this.querySelector('[data-swatch-arrow]');
    const arrowWidth = arrow ? parseFloat(getComputedStyle(arrow).width) || 16 : 0;
    const hostGap = parseFloat(getComputedStyle(this).columnGap) || 0;
    const room = width - (overflow ? 2 * (arrowWidth + hostGap) : 0) - pad;
    const visible = Math.max(1, Math.min(cap, swatches.length, Math.floor((room + gap) / unit)));
    const oldSize = this.#step;
    this.#step = visible;
    this.toggleAttribute('data-overflow', overflow);
    this.track.style.flex = `0 0 ${visible * unit - gap + pad}px`;
    if (oldSize !== visible) {
      const selected = Math.max(0, swatches.indexOf(this.#selected));
      this.page = Math.floor(selected / visible);
    }
    this.#showPage();
  }

  #showPage() {
    const swatches = [...this.querySelectorAll('[data-swatch]')];
    const last = Math.max(0, Math.ceil(swatches.length / this.#step) - 1);
    this.page = Math.min(last, this.page || 0);
    const start = this.page * this.#step;
    swatches.forEach((swatch, index) => {
      swatch.style.display = index >= start && index < start + this.#step ? '' : 'none';
    });
    this.querySelector('[command="--prev"]')?.toggleAttribute('disabled', this.page === 0);
    this.querySelector('[command="--next"]')?.toggleAttribute('disabled', this.page === last);
  }

  #apply(button) {
    const { src, srcset, url, variantId, label } = button.dataset;
    const variantLabel = this.card.querySelector('[data-card-variant-label]');
    if (variantLabel && label) {
      const suffix = variantLabel.dataset.suffix || '';
      variantLabel.textContent = suffix && !label.toLowerCase().includes(suffix.toLowerCase()) ? `${label} · ${suffix}` : label;
    }
    const image = this.card.querySelector('[data-card-image] img');
    if (image && src) {
      if (srcset) image.srcset = srcset;
      image.src = src;
    }
    if (url) {
      for (const link of this.card.querySelectorAll('a[href]')) {
        if (!this.contains(link)) link.href = url;
      }
    }
    const price = this.card.querySelector('[data-card-price]');
    const priceTemplate = this.querySelector(`template[data-price="${variantId}"]`);
    if (price && priceTemplate) price.replaceChildren(priceTemplate.content.cloneNode(true));
    const badge = this.card.querySelector('[data-card-badge]');
    const badgeTemplate = this.querySelector(`template[data-badge="${variantId}"]`);
    if (badge && badgeTemplate) badge.replaceChildren(badgeTemplate.content.cloneNode(true));
  }
}

if (!customElements.get('s-card-swatches')) {
  customElements.define('s-card-swatches', CardSwatches);
}
